'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface Report {
  code: string;
  name: string;
  description: string;
  category: string;
  lastGenerated: string;
  format: string[];
  icon: string;
}

const REPORTS: Report[] = [
  {
    code: 'RPT-001',
    name: 'Stock Position Report',
    description: 'Current stock levels, valuations, and reorder status across all sites.',
    category: 'Inventory',
    lastGenerated: '29 Jul 2026, 08:00',
    format: ['PDF', 'Excel'],
    icon: 'ChartBarIcon',
  },
  {
    code: 'RPT-002',
    name: 'Inventory Movement Report',
    description: 'All inbound and outbound movements with transaction references.',
    category: 'Inventory',
    lastGenerated: '28 Jul 2026, 18:00',
    format: ['PDF', 'Excel'],
    icon: 'ArrowsRightLeftIcon',
  },
  {
    code: 'RPT-003',
    name: 'Non-Variance Reconciliation Report',
    description: 'Physical count vs system balance with matched and unmatched items.',
    category: 'Stock Count',
    lastGenerated: '25 Jul 2026, 09:00',
    format: ['PDF'],
    icon: 'ClipboardDocumentCheckIcon',
  },
  {
    code: 'RPT-004',
    name: 'QHSE Compliance Report',
    description: 'Expiry status, inspection history, and compliance scores per site.',
    category: 'QHSE',
    lastGenerated: '27 Jul 2026, 10:30',
    format: ['PDF', 'Excel'],
    icon: 'ShieldCheckIcon',
  },
  {
    code: 'RPT-005',
    name: 'BOM Consumption Report',
    description: 'Planned vs actual material consumption per project and BOM line.',
    category: 'BOM / Projects',
    lastGenerated: '26 Jul 2026, 14:00',
    format: ['PDF', 'Excel'],
    icon: 'ClipboardDocumentListIcon',
  },
  {
    code: 'RPT-006',
    name: 'GRN Register',
    description: 'All goods receipt notes with supplier, quantity, and approval status.',
    category: 'Transactions',
    lastGenerated: '29 Jul 2026, 08:00',
    format: ['PDF', 'Excel'],
    icon: 'DocumentTextIcon',
  },
  {
    code: 'RPT-007',
    name: 'Issuance Register',
    description: 'All goods issuance records with requestor, approver, and cost centre.',
    category: 'Transactions',
    lastGenerated: '29 Jul 2026, 08:00',
    format: ['PDF', 'Excel'],
    icon: 'TruckIcon',
  },
  {
    code: 'RPT-008',
    name: 'Slow-Moving Items Report',
    description: 'Items with no movement in the last 90 days flagged for review.',
    category: 'Inventory',
    lastGenerated: '20 Jul 2026, 11:00',
    format: ['PDF', 'Excel'],
    icon: 'ExclamationTriangleIcon',
  },
];

const CATEGORIES = ['All', 'Inventory', 'Transactions', 'Stock Count', 'QHSE', 'BOM / Projects'];

const CATEGORY_COLOR: Record<string, string> = {
  Inventory: 'bg-secondary/10 text-secondary',
  Transactions: 'bg-teal/10 text-teal',
  'Stock Count': 'bg-amber-100 text-amber-700',
  QHSE: 'bg-purple-100 text-purple-700',
  'BOM / Projects': 'bg-orange-100 text-orange-700',
};

export default function ReportsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = REPORTS.filter((r) => {
    const matchCat = activeCategory === 'All' || r.category === activeCategory;
    const matchSearch =
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.code.toLowerCase().includes(search.toLowerCase()) ||
      r.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <AppLayout currentPath="/reports">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-secondary to-primary rounded-2xl text-white">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <Icon name="DocumentChartBarIcon" size={22} />
          </div>
          <div>
            <p className="font-700 text-base">Reports Centre</p>
            <p className="text-[12px] text-white/65">Generate, export, and schedule operational reports</p>
          </div>
          <div className="ml-auto hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg border border-white/20">
            <Icon name="DocumentArrowDownIcon" size={13} className="text-white/70" />
            <span className="text-[11px] text-white/80 font-500">{REPORTS.length} Reports Available</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search reports..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-[13px] bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={[
                  'px-3 py-2 rounded-xl text-[12px] font-600 transition-all',
                  activeCategory === cat
                    ? 'bg-secondary text-white shadow-sm'
                    : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-secondary/30',
                ].join(' ')}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Reports Grid */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Icon name="DocumentMagnifyingGlassIcon" size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-500">No reports match your search</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((report) => (
              <div
                key={report.code}
                className="bg-card border border-border rounded-2xl shadow-card p-5 flex flex-col gap-4 hover:border-secondary/30 hover:shadow-md transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary/8 flex items-center justify-center shrink-0">
                    <Icon name={report.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-700 text-muted-foreground font-mono">{report.code}</span>
                      <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${CATEGORY_COLOR[report.category] ?? 'bg-muted text-muted-foreground'}`}>
                        {report.category}
                      </span>
                    </div>
                    <p className="text-[13px] font-700 text-foreground leading-snug">{report.name}</p>
                  </div>
                </div>

                <p className="text-[12px] text-muted-foreground leading-relaxed">{report.description}</p>

                <div className="flex items-center justify-between pt-1 border-t border-border">
                  <div>
                    <p className="text-[10px] text-muted-foreground/60">Last generated</p>
                    <p className="text-[11px] font-600 text-muted-foreground">{report.lastGenerated}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {report.format.map((fmt) => (
                      <button
                        key={fmt}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-secondary/8 hover:bg-secondary text-secondary hover:text-white rounded-lg text-[11px] font-600 transition-all"
                      >
                        <Icon name="ArrowDownTrayIcon" size={11} />
                        {fmt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
